import {useEffect, useMemo} from 'react';
import {createSelector} from 'reselect';

import {useMetricKeySearch} from '../../components/MetricSearch/useMetricKeySearch';
import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {convertMetricSearchResultsToKeyInfo} from '../../components/WorkspaceDrawer/PanelsQuickAdd/hooks/useSearchedKeys';
import {useIsNgwEnabled} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {escapeRegexChars} from '../../util/regex';
import {useSelector} from '../hooks';
import {makePartSelector} from '../views/selectors';
import {
  useResetPanelVisibilityKeys,
  useSetPanelVisibilityKeysQueryResults,
  useSetPanelVisibilityKeysRequested,
} from './panelVisibilityKeysStore';
import {getOverriddenPanelDisplayedMetrics} from './useRequiredMetrics';

/**
 * Max bytes of `metricNamePattern` sent for one lookup. clickhouse-go inlines
 * the bound pattern into the query text, which counts against ClickHouse's
 * default 256 KiB `max_query_size`; leave half for the rest of the query.
 */
const PANEL_VISIBILITY_PATTERN_BYTE_BUDGET = 128 * 1024;
const textEncoder = new TextEncoder();

/** RE2 pattern matching exactly the given keys and nothing else. */
export function exactKeysPattern(keys: readonly string[]): string {
  return `^(?:${keys.map(escapeRegexChars).join('|')})$`;
}

/**
 * The keys one visibility lookup can ask for. Keys that do not fit stay
 * requested but unanswered, so they are not treated as having no data.
 */
export function keysWithinLookupLimit(
  keys: readonly string[]
): readonly string[] {
  const queried: string[] = [];
  // Account for `^(?:`, `)$`, and each `|` separator.
  let patternBytes = 6;
  for (const key of keys) {
    const escapedKeyBytes = textEncoder.encode(
      escapeRegexChars(key)
    ).byteLength;
    const separatorBytes = queried.length === 0 ? 0 : 1;
    if (
      patternBytes + separatorBytes + escapedKeyBytes >
      PANEL_VISIBILITY_PATTERN_BYTE_BUDGET
    ) {
      continue;
    }
    patternBytes += separatorBytes + escapedKeyBytes;
    queried.push(key);
  }
  return queried.length === keys.length ? keys : queried;
}

/**
 * Looks up metrics used by edited auto panels so we can check if there's data
 * to show in those panels. Results are recorded in `panelVisibilityKeysStore`.
 *
 * This is the "visibility" half of the history key set under NGW:
 * PGHK samples the keys to generate panels from, while this `metricSearch`
 * answers "does this metric have data" for panels using keys that may lie
 * outside the panel gen sample.
 */
export function usePanelVisibilityKeysSync(): void {
  const isNgwEnabled = useIsNgwEnabled();
  const {panelBankConfigRef, runSetRefs, customRunNamesRef} =
    useWorkspaceRefsContext();
  const configOverridesSelector = useMemo(
    () =>
      createSelector(
        makePartSelector(panelBankConfigRef),
        panelBankConfig => panelBankConfig.panelConfigOverrides
      ),
    [panelBankConfigRef]
  );
  const configOverrides = useSelector(configOverridesSelector);
  const requested = useMemo(
    () =>
      isNgwEnabled ? getOverriddenPanelDisplayedMetrics(configOverrides) : [],
    [isNgwEnabled, configOverrides]
  );
  const queried = useMemo(() => keysWithinLookupLimit(requested), [requested]);

  const skip = queried.length === 0;
  const {results, loading, error} = useMetricKeySearch(
    runSetRefs,
    customRunNamesRef,
    {
      metricNamePattern: skip ? undefined : exactKeysPattern(queried),
      pageSize: queried.length,
      skip,
    }
  );

  const setRequested = useSetPanelVisibilityKeysRequested();
  const setQueryResults = useSetPanelVisibilityKeysQueryResults();
  const reset = useResetPanelVisibilityKeys();

  // Clear store on unmounts so results cannot leak between workspaces
  useEffect(() => reset, [reset]);

  useEffect(() => {
    setRequested(requested);
  }, [requested, setRequested]);

  const settled = !skip && !loading && error == null;
  useEffect(() => {
    if (!settled) {
      return;
    }
    setQueryResults(convertMetricSearchResultsToKeyInfo(results), queried);
  }, [settled, results, queried, setQueryResults]);
}
